import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { CohortMap } from "@/app/analyticsQueries";
import type { MentorLoad } from "@/app/peopleQueries";
import { RequestError } from "@/app/queries";
import { count } from "@/lib/plural";
import { people } from "./model";

function Card({ eyebrow, tone, children, testId }: { eyebrow: string; tone?: "warn"; children: ReactNode; testId: string }) {
  return (
    <section className="rounded-md bg-card" style={{ border: `1px solid ${tone === "warn" ? "var(--warn)" : "var(--bd)"}` }} data-testid={testId}>
      <div className={`border-b border-bd px-3.5 py-[9px] font-mono text-[10.5px] uppercase tracking-[.06em] ${tone === "warn" ? "text-warn" : "text-mut"}`}>{eyebrow}</div>
      <div className="flex flex-col gap-2.5 px-3.5 py-3">{children}</div>
    </section>
  );
}

/** «Где встаёт когорта»: три шага с наибольшим числом студентов; «N человек» — из данных. */
export function Bottlenecks({ map }: { map: CohortMap }) {
  const total = map.rows.length;
  return (
    <Card eyebrow="где встаёт когорта" testId="bottlenecks">
      <p className="m-0 text-[11.5px] text-mut">шаги, на которых сейчас больше всего студентов</p>
      {map.bottlenecks.length === 0 ? (
        <span className="font-mono text-[12px] text-ink2">пока никто не идёт по маршруту</span>
      ) : (
        <ul className="m-0 flex flex-col gap-2.5 p-0">
          {map.bottlenecks.map((b) => (
            <li key={b.step.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1" data-testid="bottleneck">
              <span className="truncate text-[13px] text-ink">{b.step.title}</span>
              <span className="font-mono text-[12px] text-ink2" data-testid="bottleneck-count">
                {people(b.students)}
              </span>
              <span className="font-mono text-[10.5px] text-mut">
                {b.step.track} · шаг {b.step.index}
              </span>
              <span className="h-[5px] w-[84px] overflow-hidden rounded-[3px] bg-card2" aria-hidden>
                <span className="block h-full bg-accdim" style={{ width: `${total ? Math.round((b.students / total) * 100) : 0}%` }} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** «Требуют внимания»: застрявшие с причиной из API («на шаге «…» N дн.»). */
export function Attention({ map }: { map: CohortMap }) {
  return (
    <Card eyebrow="требуют внимания" tone="warn" testId="attention">
      {map.attention.length === 0 ? (
        <span className="text-[12.5px] text-ink2">никто не стоит дольше 10 дней — сигналов нет</span>
      ) : (
        <ul className="m-0 flex flex-col gap-3 p-0">
          {map.attention.map((a) => (
            <li key={a.student.id} className="flex flex-col gap-0.5" data-testid="attention-item">
              <Link to="/mentor/student/$userId" params={{ userId: a.student.id }} className="text-[13px] font-medium text-ink no-underline hover:underline">
                {a.student.name}
              </Link>
              <span className="text-[12px] leading-[1.45] text-ink2">{a.why}</span>
              <span className="font-mono text-[10.5px] text-mut">ментор {a.student.mentor || "не назначен"}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

type LoadQ = { data?: MentorLoad; error?: unknown; refetch: () => unknown };

/** Нагрузка менторов — /admin/mentor-load в том же режиме; полоса n/max из данных. */
export function MentorLoadCard({ q }: { q: LoadQ }) {
  return (
    <Card eyebrow="нагрузка менторов" testId="mentor-load">
      {q.error ? (
        <div className="flex flex-col gap-2">
          <span className="text-[12px] text-ink2">{q.error instanceof RequestError && q.error.status === 403 ? "нагрузку видят только менторы и администраторы" : "нагрузка не загрузилась — остальная карта актуальна"}</span>
          {!(q.error instanceof RequestError && q.error.status === 403) && (
            <button type="button" onClick={() => void q.refetch()} className="self-start rounded-sm border border-bd2 bg-transparent px-2.5 py-1 font-mono text-[11px] text-ink">
              повторить
            </button>
          )}
        </div>
      ) : !q.data ? (
        <span aria-busy="true" className="h-2.5 w-2/3 rounded-[3px] bg-card2" />
      ) : (
        <LoadList load={q.data} />
      )}
    </Card>
  );
}

function LoadList({ load }: { load: MentorLoad }) {
  if (load.mentors.length === 0) return <span className="text-[12.5px] text-ink2">в этом режиме у студентов нет менторов</span>;
  return (
    <ul className="m-0 flex flex-col gap-2.5 p-0">
      {load.mentors.map((m) => (
        <li key={m.mentor.id} className="flex flex-col gap-1" data-testid="load-row">
          <span className="flex items-baseline justify-between font-mono text-[12px]">
            <span className="text-ink">{m.mentor.name}</span>
            <span className="text-ink2">{count(m.students, "студент", "студента", "студентов")}</span>
          </span>
          <span className="h-[5px] overflow-hidden rounded-[3px] bg-card2" aria-hidden>
            <span className={`block h-full ${load.skewed && m.students === load.max ? "bg-warn" : "bg-acc"}`} style={{ width: `${load.max ? Math.round((m.students / load.max) * 100) : 0}%` }} />
          </span>
        </li>
      ))}
      {load.unassigned > 0 && <li className="font-mono text-[11px] text-warn">без ментора: {people(load.unassigned)}</li>}
      {load.note && <li className={`text-[11.5px] ${load.skewed ? "text-warn" : "text-mut"}`}>{load.note}</li>}
    </ul>
  );
}
